import { ArrowLeft, KeyRound, Loader2, Mail, Shield } from 'lucide-react';
import { type FormEvent, useState } from 'react';
import { AdminAuthError, type AdminAuthErrorCode, sendOtpCode, verifyOtpCode } from '../lib/admin-auth';
import { useAuthStore } from '../lib/auth-store';

interface LoginProps {
  error?: string | null;
}

const ERROR_MESSAGES: Record<AdminAuthErrorCode, string> = {
  rate_limited: 'Too many attempts. Wait a moment before trying again.',
  invalid_email: 'Enter a valid work email address.',
  connection_disabled: 'Email sign-in is temporarily unavailable. Contact a platform administrator.',
  invalid_code: 'That code is invalid or expired. Request a new one.',
  not_provisioned: 'Your account is not provisioned for the admin panel. Contact a platform administrator.',
  unknown: 'Something went wrong. Please try again.',
};

function messageFor(err: unknown): string {
  if (err instanceof AdminAuthError) {
    return ERROR_MESSAGES[err.code] ?? ERROR_MESSAGES.unknown;
  }
  return ERROR_MESSAGES.unknown;
}

const inputClass =
  'w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-text placeholder:text-text-muted focus:outline-none focus:border-primary transition-colors';
const buttonClass =
  'flex w-full items-center justify-center gap-2 rounded-lg bg-primary py-2.5 text-sm font-medium text-background transition-colors hover:bg-primary-hover disabled:opacity-50';

export function Login({ error }: LoginProps) {
  const setAuth = useAuthStore((s) => s.setAuth);
  const [step, setStep] = useState<'email' | 'code'>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  const displayedError = localError ?? error ?? null;

  const handleSendCode = async (event: FormEvent) => {
    event.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) return;

    setSubmitting(true);
    setLocalError(null);
    try {
      await sendOtpCode(trimmed);
      setStep('code');
    } catch (err) {
      setLocalError(messageFor(err));
    } finally {
      setSubmitting(false);
    }
  };

  const handleVerify = async (event: FormEvent) => {
    event.preventDefault();
    const trimmedCode = code.trim();
    if (!trimmedCode) return;

    setSubmitting(true);
    setLocalError(null);
    try {
      const session = await verifyOtpCode(email.trim(), trimmedCode);
      setAuth(session.token, session.user);
    } catch (err) {
      setLocalError(messageFor(err));
    } finally {
      setSubmitting(false);
    }
  };

  const goBackToEmail = () => {
    setStep('email');
    setCode('');
    setLocalError(null);
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="w-full max-w-sm">
        <div className="mb-8 text-center">
          <h1 className="text-2xl font-semibold text-text">FlowSellAI</h1>
          <p className="mt-1 text-sm text-text-secondary">Admin Panel</p>
        </div>

        <div className="rounded-xl border border-border bg-surface p-6 space-y-5">
          {displayedError ? (
            <div
              role="alert"
              className="rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-400"
            >
              {displayedError}
            </div>
          ) : null}

          <div className="flex items-center justify-center gap-2 text-text-secondary text-sm">
            <Shield size={16} />
            <span>Super admin access required</span>
          </div>

          {step === 'email' ? (
            <form onSubmit={handleSendCode} className="space-y-4">
              <div className="space-y-1.5">
                <label htmlFor="admin-email" className="block text-sm text-text-secondary">
                  Work email
                </label>
                <input
                  id="admin-email"
                  type="email"
                  autoComplete="email"
                  autoFocus
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@flowsoftdev.com"
                  className={inputClass}
                />
              </div>

              <button type="submit" disabled={submitting} className={buttonClass}>
                {submitting ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Mail size={16} />
                    Send code
                  </>
                )}
              </button>

              <p className="text-center text-xs text-text-secondary">
                We&apos;ll send a one-time code to your work email.
              </p>
            </form>
          ) : (
            <form onSubmit={handleVerify} className="space-y-4">
              <div className="space-y-1.5">
                <label htmlFor="admin-code" className="block text-sm text-text-secondary">
                  One-time code
                </label>
                <input
                  id="admin-code"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  autoFocus
                  required
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder="123456"
                  className={inputClass}
                />
                <p className="text-xs text-text-secondary">
                  Sent to <span className="text-text">{email.trim()}</span>
                </p>
              </div>

              <button type="submit" disabled={submitting} className={buttonClass}>
                {submitting ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Verifying...
                  </>
                ) : (
                  <>
                    <KeyRound size={16} />
                    Verify &amp; sign in
                  </>
                )}
              </button>

              <button
                type="button"
                onClick={goBackToEmail}
                disabled={submitting}
                className="flex w-full items-center justify-center gap-1.5 text-xs text-text-secondary hover:text-text transition-colors disabled:opacity-50"
              >
                <ArrowLeft size={14} />
                Use a different email
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
